/**
 * Typed client for monitoring a company can see and act on (`/api/v1`; docs/API.md "Monitoring"): outcomes per
 * channel with findings and history, cleared alarms, the Failed tab's root causes and bulk actions, the company's
 * own dead letters, and "Not answered here" per channel. The console's mock implements the same interface.
 */
import type { AlarmDetail, ChannelCoverageInput, CompanyDeadJobView, FailedGroupBulkInput, FailedGroupView, OutcomesView } from '@takeit4me/shared'
import { Http, type HttpOptions } from './http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
export type FailedBulkBody = In<typeof FailedGroupBulkInput>
export type ChannelCoverage = In<typeof ChannelCoverageInput>['coverage']
export interface AlarmHistoryView { id: string; kind: string; subject: string; message: string; severity: string; status: string; detail: AlarmDetail; opened_at: string; resolved_at: string | null; acknowledged_at: string | null }

export interface MonitoringApi {
  outcomes(weeks?: number): Promise<OutcomesView>
  /** Runs the outcome checks now (admins), then returns the page. */
  checkOutcomes(): Promise<OutcomesView>
  /** The last 30 alarms that cleared. */
  alarmHistory(): Promise<AlarmHistoryView[]>
  failedSummary(): Promise<FailedGroupView[]>
  retryFailed(body: FailedBulkBody): Promise<{ count: number }>
  dismissFailed(body: FailedBulkBody): Promise<{ count: number }>
  deadJobs(): Promise<CompanyDeadJobView[]>
  settleDeadJob(id: string, action: 'retry' | 'resolve'): Promise<{ ok: true }>
  setChannelCoverage(channelId: string, coverage: ChannelCoverage): Promise<{ ok: true; coverage: ChannelCoverage }>
  /** Takes an address off Postmark's inactive list on every Postmark channel (after a 406 "inactive recipient"). */
  reactivateEmail(address: string): Promise<{ results: { channel: string; ok: boolean; detail: string }[] }>
}

const enc = encodeURIComponent
export class MonitoringClient implements MonitoringApi {
  private http: Http
  constructor(opts: HttpOptions) { this.http = new Http({ ...opts, baseUrl: `${opts.baseUrl.replace(/\/$/, '')}/api/v1` }) }
  outcomes(weeks?: number) { return this.http.get<OutcomesView>('/monitoring/outcomes', { weeks }) }
  checkOutcomes() { return this.http.post<OutcomesView>('/monitoring/outcomes/check', {}) }
  alarmHistory() { return this.http.get<AlarmHistoryView[]>('/alarms/history') }
  failedSummary() { return this.http.get<FailedGroupView[]>('/email/failed/summary') }
  retryFailed(body: FailedBulkBody) { return this.http.post<{ count: number }>('/email/failed/retry', body) }
  dismissFailed(body: FailedBulkBody) { return this.http.post<{ count: number }>('/email/failed/dismiss', body) }
  deadJobs() { return this.http.get<CompanyDeadJobView[]>('/dead-jobs') }
  settleDeadJob(id: string, action: 'retry' | 'resolve') { return this.http.post<{ ok: true }>(`/dead-jobs/${enc(id)}/${action}`, {}) }
  reactivateEmail(address: string) { return this.http.post<{ results: { channel: string; ok: boolean; detail: string }[] }>('/email/reactivate', { address }) }
  setChannelCoverage(channelId: string, coverage: ChannelCoverage) { return this.http.put<{ ok: true; coverage: ChannelCoverage }>(`/channels/${enc(channelId)}/coverage`, { coverage }) }
}
